import React, { useImperativeHandle, useState, useEffect, useCallback, forwardRef, memo } from 'react';

import classy from '@core/utils/classy';

import './style.scss';

interface TextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'value'> {
  /** Expand the text area vertically to fit its content */
  autoGrow?: 'always' | 'on-focus';
  className?: string;
  kind?: 'default' | 'ghost' | 'text';
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
  resize?: 'both' | 'horizontal' | 'none' | 'vertical';
  size?: 'lg' | 'md' | 'sm';
  value?: React.TextareaHTMLAttributes<HTMLTextAreaElement>['value'] | null;
}

const Textarea = memo(
  forwardRef<HTMLTextAreaElement | undefined, TextareaProps>(
    (
      {
        autoGrow,
        className,
        kind = 'default',
        onChange,
        resize = 'vertical',
        size = 'md',
        ...attributes
      }: TextareaProps,
      forwardedRef,
    ) => {
      const [value, setValue] = useState(attributes.value);
      const [textareaEl, setTextareaEl] = useState<HTMLTextAreaElement | null>(null);

      useImperativeHandle(forwardedRef, () => textareaEl || undefined);

      useEffect(() => {
        setValue(attributes.value);
      }, [attributes.value]);

      const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = useCallback(
        event => {
          setValue(event.target.value);
          onChange?.(event);
          if (autoGrow && textareaEl) {
            textareaEl.style.height = 'auto';
            textareaEl.style.height = `${textareaEl.scrollHeight}px`;
          }
        },
        [onChange, autoGrow, textareaEl],
      );

      useEffect(() => {
        if (!autoGrow || autoGrow === 'on-focus' || !textareaEl) return;
        textareaEl.style.height = `${textareaEl.scrollHeight}px`;
        textareaEl.style.overflowY = 'hidden';
      }, [autoGrow, textareaEl]);

      return (
        <textarea
          {...attributes}
          ref={setTextareaEl}
          className={classy(
            'Textarea',
            `Textarea_${size}`,
            `Textarea_${resize}`,
            kind !== 'default' && `Textarea_${kind}`,
            className,
          )}
          {...(autoGrow === 'on-focus' && {
            onFocus: () => {
              if (textareaEl) textareaEl.style.height = `${textareaEl.scrollHeight}px`;
            },
          })}
          onChange={handleChange}
          value={value || ''}
        />
      );
    },
  ),
);

export default Textarea;
